import React, { useState } from 'react';

interface ResolutionData {
  id: string;
  title: string;
  councilName: string;
  meetingDate: string;
  location: string;
  chairperson: string;
  secretary: string;
  preamble: string;
  resolutions: string[];
  signatories: { name: string; title: string; village: string }[];
}

const DEFAULT_RESOLUTION: ResolutionData = {
  id: 'res-2026-001',
  title: 'RESOLUTION ON UZAIRUE GLOBAL DIASPORA ENDOWMENT FUND & FESTIVAL SECURITY',
  councilName: 'Uzairue Supreme Royal Traditional Council & Diaspora Delegates Assembly',
  meetingDate: 'August 12, 2026',
  location: 'Ogieneni Royal Palace Hall, Jattu-Uzairue, Edo State',
  chairperson: 'High Chief Danialu Momoh',
  secretary: 'Dr. Fatima Afegbua',
  preamble: 'AT THE EXTRAORDINARY CONVENING of the Uzairue Royal Traditional Council in joint session with international delegates of the USA, UK, European, and Asian Diaspora Chapters:',
  resolutions: [
    'THAT the annual ERUE Festival 2026 shall officially commence on December 24, 2026, featuring the 22-Village Masquerade Procession and Grand Raffle Draw.',
    'THAT a dedicated Uzairue Kingdom Youth Empowerment & Technology Scholarship Fund be established with 60% of proceeds from the Grand Raffle Draw.',
    'THAT a joint security taskforce comprising local community vigilantes, police escorts, and royal guards be deployed along the Benin-Auchi expressway for returning diaspora visitors.',
    'THAT all 22 village heads be mandated to submit lists of age-grade initiates to the Central Steering Committee by September 15, 2026.',
  ],
  signatories: [
    { name: 'HRH Ogieneni of Uzairue', title: 'Paramount Royal Father & Assenter', village: 'Jattu Palace' },
    { name: 'High Chief Danialu Momoh', title: 'Chairman, Festival Steering Committee', village: 'Jattu-Uzairue' },
    { name: 'Dr. Fatima Afegbua', title: 'President, USA & Canada Diaspora Union', village: 'Afashio-Uzairue' },
  ],
};

export const ResolutionGenerator: React.FC = () => {
  const [data, setData] = useState<ResolutionData>(DEFAULT_RESOLUTION);
  const [newResolutionText, setNewResolutionText] = useState('');

  const handleAddResolution = () => {
    if (!newResolutionText.trim()) return;
    setData({
      ...data,
      resolutions: [...data.resolutions, newResolutionText.trim()],
    });
    setNewResolutionText('');
  };

  const handleRemoveResolution = (index: number) => {
    setData({
      ...data,
      resolutions: data.resolutions.filter((_, i) => i !== index),
    });
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 md:p-8 shadow-2xl space-y-8 text-white">
      {/* Top Banner */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-slate-800 pb-6 print:hidden">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-2xl">📜</span>
            <h2 className="text-xl md:text-2xl font-serif font-black text-amber-300">
              Royal Council Resolutions & Meeting Minutes Composer
            </h2>
            <span className="bg-amber-400 text-slate-950 text-[10px] px-2.5 py-0.5 rounded-full font-bold uppercase font-mono">
              Printable Official Document
            </span>
          </div>
          <p className="text-xs text-slate-300 mt-1">
            Draft, format, sign, and print official Uzairue Council resolutions and diaspora meeting summaries directly.
          </p>
        </div>

        <button
          onClick={handlePrint}
          className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-black px-5 py-2.5 rounded-xl text-xs uppercase tracking-wider transition-all cursor-pointer flex items-center gap-1.5 shadow-lg"
        >
          <span>🖨️ Print / Save PDF Document</span>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 print:block">
        
        {/* Left Column: Form Controls (Hidden during print) */}
        <div className="lg:col-span-5 space-y-5 print:hidden">
          <div className="bg-slate-950 border border-slate-800 p-5 rounded-2xl space-y-4">
            <h3 className="text-xs font-bold text-amber-400 uppercase tracking-wider flex items-center gap-2">
              <span>✍️</span> Document Settings
            </h3>

            <div>
              <label className="block text-[11px] font-bold text-slate-400 mb-1">Document Title</label>
              <input
                type="text"
                value={data.title}
                onChange={(e) => setData({ ...data, title: e.target.value })}
                className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:border-amber-400 outline-none font-bold"
              />
            </div>

            <div>
              <label className="block text-[11px] font-bold text-slate-400 mb-1">Council / Assembly Name</label>
              <input
                type="text"
                value={data.councilName}
                onChange={(e) => setData({ ...data, councilName: e.target.value })}
                className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:border-amber-400 outline-none"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-bold text-slate-400 mb-1">Meeting Date</label>
                <input
                  type="text"
                  value={data.meetingDate}
                  onChange={(e) => setData({ ...data, meetingDate: e.target.value })}
                  className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:border-amber-400 outline-none"
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-400 mb-1">Location</label>
                <input
                  type="text"
                  value={data.location}
                  onChange={(e) => setData({ ...data, location: e.target.value })}
                  className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:border-amber-400 outline-none"
                />
              </div>
            </div>
          </div>

          {/* Add Resolution Item */}
          <div className="bg-slate-950 border border-slate-800 p-5 rounded-2xl space-y-3">
            <h3 className="text-xs font-bold text-emerald-400 uppercase tracking-wider flex items-center gap-2">
              <span>➕</span> Add Resolution Clause
            </h3>

            <textarea
              value={newResolutionText}
              onChange={(e) => setNewResolutionText(e.target.value)}
              rows={3}
              className="w-full bg-slate-900 border border-slate-700 rounded-xl p-3 text-xs text-white focus:border-amber-400 outline-none"
              placeholder="e.g. THAT all 22 villages shall contribute..."
            />

            <button
              onClick={handleAddResolution}
              className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-2 rounded-xl text-xs uppercase tracking-wider transition-all cursor-pointer"
            >
              Add Resolution Clause
            </button>
          </div>
        </div>

        {/* Right Column: Printable Paper Preview */}
        <div className="lg:col-span-7 bg-white text-slate-900 p-8 sm:p-12 rounded-3xl shadow-2xl border-4 border-amber-400 space-y-6 font-serif print:w-full print:shadow-none print:border-none print:p-0">
          
          {/* Header */}
          <div className="text-center space-y-2 border-b-2 border-slate-900 pb-6">
            <div className="w-16 h-16 mx-auto bg-emerald-950 rounded-full border-2 border-amber-500 flex items-center justify-center text-amber-300 font-bold text-2xl shadow">
              👑
            </div>
            <h1 className="text-xl font-black text-slate-950 uppercase tracking-wide">
              UZAIRUE KINGDOM TRADITIONAL COUNCIL
            </h1>
            <p className="text-xs font-bold text-amber-800 tracking-widest uppercase">
              {data.councilName}
            </p>
            <p className="text-[11px] text-slate-600 font-sans">
              Ogieneni Royal Palace, Jattu-Uzairue, Edo State, Nigeria
            </p>
          </div>

          {/* Title */}
          <div className="text-center bg-amber-50 border-y border-amber-300 py-3">
            <h2 className="text-sm font-black text-amber-950 uppercase tracking-wider">
              {data.title}
            </h2>
            <p className="text-[10px] text-slate-600 font-sans mt-0.5">
              Ref No: UZC/RES/2026/042 • Date: {data.meetingDate}
            </p>
          </div>

          {/* Preamble */}
          <p className="text-xs leading-relaxed text-slate-800 font-sans font-medium text-justify">
            {data.preamble}
          </p>

          {/* Resolution Clauses List */}
          <div className="space-y-3 pt-2 font-sans">
            <h3 className="text-xs font-black uppercase tracking-wider text-slate-900 border-b border-slate-200 pb-1">
              IT IS HEREBY RESOLVED AND ENACTED AS FOLLOWS:
            </h3>

            <ol className="list-decimal list-inside space-y-2.5 text-xs text-slate-900 leading-relaxed">
              {data.resolutions.map((res, i) => (
                <li key={i} className="pl-1 group relative">
                  <span>{res}</span>
                  <button
                    onClick={() => handleRemoveResolution(i)}
                    className="ml-2 text-[10px] text-red-600 font-bold opacity-0 group-hover:opacity-100 transition-opacity print:hidden"
                    title="Remove item"
                  >
                    [Delete]
                  </button>
                </li>
              ))}
            </ol>
          </div>

          {/* Signatures */}
          <div className="pt-8 border-t border-slate-300 space-y-4 font-sans">
            <h4 className="text-[11px] font-black uppercase text-slate-800 text-center">
              IN WITNESS WHEREOF, THE UNDERSIGNED DULY ATTEST AND AFFIX THEIR SIGNATURES:
            </h4>

            <div className="grid grid-cols-3 gap-4 pt-4 text-center">
              {data.signatories.map((sig, i) => (
                <div key={i} className="space-y-1">
                  <div className="border-b border-slate-900 h-10 flex items-end justify-center pb-1">
                    <span className="font-serif italic text-xs text-amber-900 font-bold">
                      {sig.name}
                    </span>
                  </div>
                  <p className="text-[10px] font-bold text-slate-900">{sig.name}</p>
                  <p className="text-[9px] text-slate-600">{sig.title}</p>
                </div>
              ))}
            </div>
          </div>

        </div>

      </div>
    </div>
  );
};

export default ResolutionGenerator;
